import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TIEMPOS, duracion } from '../salon/mesas';
import { Estacion, useKds } from './useKds';

const ESTACIONES: [Estacion, string][] = [['todas', 'Todas'], ['cocina', 'Cocina'], ['barra', 'Barra']];
const COLUMNAS = [
  { k: 'pendiente', label: 'Pendiente', acento: 'var(--muted)', accion: 'Empezar' },
  { k: 'preparacion', label: 'En preparación', acento: 'var(--warn)', accion: 'Marcar lista' },
  { k: 'lista', label: 'Lista', acento: 'var(--ok)', accion: 'Entregar' },
] as const;
const RELOJ_MS = 15_000;

const minutos = (iso: string) => (Date.now() - new Date(iso).getTime()) / 60000;

export default function Comandas() {
  const { usuario } = useAuth();
  const [params, setParams] = useSearchParams();
  // Un cocinero asignado a una sola estación solo ve la suya.
  const fija = usuario?.rol === 'cocina' && usuario.estacion && usuario.estacion !== 'ambas' ? usuario.estacion as Estacion : null;
  const permitidas = fija ? ESTACIONES.filter(([k]) => k === fija) : ESTACIONES;
  const pedida = params.get('e') as Estacion | null;
  const estacion: Estacion = fija || (pedida && permitidas.some(([k]) => k === pedida) ? pedida : 'todas');
  const k = useKds(estacion);
  const [verTerminadas, setVerTerminadas] = useState(false);
  const [ahora, setAhora] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setAhora(new Date()), RELOJ_MS); return () => clearInterval(t); }, []);

  const columna = (c: any) => k.terminada(c) ? 'lista' : c.estado === 'pendiente' ? 'pendiente' : 'preparacion';
  const archivadas = k.comandas.filter(k.archivada).length;
  const visibles = k.comandas.filter(c => verTerminadas || !k.archivada(c));

  return (
    <div className="tema-oscuro" style={{ margin: '-22px -24px', height: 'calc(100% + 44px)', display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--ink)' }}>
      <div style={{ minHeight: 64, flex: 'none', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px', padding: '10px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>Comandas</h1>
        {permitidas.length > 1 && (
          <div role="tablist" aria-label="Estación" style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--well)', borderRadius: 11 }}>
            {permitidas.map(([e, l]) => (
              <button key={e} role="tab" aria-selected={estacion === e} onClick={() => setParams(e === 'todas' ? {} : { e })}
                style={{ height: 44, padding: '0 18px', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer', background: estacion === e ? 'var(--bg)' : 'transparent', color: estacion === e ? 'var(--ink)' : 'var(--muted)' }}>{l}</button>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {archivadas > 0 && (
          <button onClick={() => setVerTerminadas(v => !v)} aria-pressed={verTerminadas}
            style={{ height: 44, padding: '0 14px', borderRadius: 9, border: '1px solid var(--line-strong)', background: 'var(--well)', color: 'var(--ink)', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            {verTerminadas ? 'Ocultar entregadas' : `Ver entregadas (${archivadas})`}
          </button>
        )}
        <div aria-label="Hora" style={{ fontSize: 22, fontWeight: 700 }}>{ahora.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })}</div>
      </div>

      {k.aviso && (
        <div role="alert" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', fontSize: 17, fontWeight: 700,
          background: k.aviso.tipo === 'cancelada' ? 'var(--danger-bg)' : 'var(--warn-bg)', color: k.aviso.tipo === 'cancelada' ? 'var(--danger)' : 'var(--warn)' }}>
          <span style={{ flex: 1 }}>{k.aviso.texto}</span>
          <button onClick={k.cerrarAviso} aria-label="Cerrar aviso" style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <main style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1, background: 'var(--line)' }}>
        {COLUMNAS.map(col => {
          const tarjetas = visibles.filter(c => columna(c) === col.k).sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
          return (
            <section key={col.k} aria-label={col.label} style={{ minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
              <div style={{ flex: 'none', padding: '14px 18px', display: 'flex', alignItems: 'baseline', gap: 10, background: 'var(--surface)', borderBottom: `2px solid ${col.acento}` }}>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{col.label}</h2>
                <span style={{ fontSize: 16, color: 'var(--muted)' }}>{tarjetas.length}</span>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!tarjetas.length && <div style={{ padding: '30px 10px', textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Sin comandas.</div>}
                {tarjetas.map(c => (
                  <Tarjeta key={c.id} c={c} col={col} nueva={k.nuevas.has(c.id)} umbrales={k.umbrales} archivada={k.archivada(c)}
                    onAccion={() => col.k === 'pendiente' ? k.cambiarEstado(c.id, 'en_preparacion') : col.k === 'preparacion' ? k.cambiarEstado(c.id, 'lista') : k.entregar(c.id)}
                    onRegresar={col.k === 'lista' ? () => k.cambiarEstado(c.id, 'en_preparacion') : undefined} />
                ))}
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}

function Tarjeta({ c, col, nueva, umbrales, archivada, onAccion, onRegresar }: {
  c: any; col: typeof COLUMNAS[number]; nueva: boolean; umbrales: [number, number]; archivada: boolean;
  onAccion: () => void; onRegresar?: () => void;
}) {
  const m = minutos(c.created_at);
  const tarde = m >= umbrales[1];
  const colorTiempo = tarde ? 'var(--danger)' : m >= umbrales[0] ? 'var(--warn)' : 'var(--muted)';
  // Solo se ven los tiempos que el mesero ya mandó a preparar.
  const enviados: number[] = c.tiempos_enviados || [];
  const items = enviados.length ? c.items.filter((i: any) => enviados.includes(i.tiempo || 1)) : c.items;
  const variosTiempos = new Set(c.items.map((i: any) => i.tiempo || 1)).size > 1;
  const ronda = (c.ronda || 1) > 1;
  const titulo = c.tipo === 'para_llevar' ? `Para llevar${c.cliente_nombre ? ` · ${c.cliente_nombre}` : ''}` : `Mesa ${c.mesa_numero}`;

  return (
    <article aria-label={titulo} style={{
      background: 'var(--surface)', borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
      border: `2px solid ${tarde ? 'var(--danger)' : 'var(--line)'}`, opacity: archivada ? .55 : 1,
      boxShadow: nueva ? '0 0 0 3px var(--brand-texto)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 20, fontWeight: 700 }}>
          {titulo}
          {ronda && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--brand)', color: 'var(--on-brand)', verticalAlign: 'middle' }}>RONDA {c.ronda}</span>}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: colorTiempo, whiteSpace: 'nowrap' }}>{duracion(Math.floor(m))}</span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--muted)' }}>{c.mesero}</div>
      {c.notas && <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--warn)' }}>⚑ {c.notas}</div>}

      {!items.length && <div style={{ fontSize: 15, color: 'var(--muted)', fontStyle: 'italic' }}>Sin productos enviados todavía.</div>}
      {items.map((i: any) => (
        <div key={i.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 0', borderTop: '1px solid var(--line)', opacity: i.estado === 'listo' && col.k !== 'lista' ? .45 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 18, fontWeight: 700, width: 28 }}>{i.cantidad}×</span>
            <span style={{ fontSize: 17, flex: 1, textDecoration: i.estado === 'listo' && col.k !== 'lista' ? 'line-through' : 'none' }}>{i.producto}</span>
            {(variosTiempos || (i.tiempo || 1) > 1) && (
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--well)', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{TIEMPOS[i.tiempo || 1]}</span>
            )}
          </div>
          {i.modificadores?.filter((x: any) => x?.nombre).map((x: any, n: number) => (
            <div key={n} style={{ fontSize: 15, fontWeight: 700, paddingLeft: 36, color: x.operacion === 'quitar' ? 'var(--danger)' : 'var(--info)' }}>
              {x.operacion === 'quitar' ? 'SIN' : '+'} {x.nombre}
            </div>
          ))}
          {i.notas && <div style={{ fontSize: 15, color: 'var(--warn)', paddingLeft: 36 }}>⚑ {i.notas}</div>}
        </div>
      ))}

      <button onClick={onAccion} style={{ height: 48, borderRadius: 10, border: 'none', fontSize: 16, fontWeight: 700, cursor: 'pointer', color: 'var(--on-brand)', background: col.k === 'lista' ? 'oklch(0.5 0.1 150)' : 'var(--brand)' }}>{col.accion}</button>
      {onRegresar && (
        <button onClick={onRegresar} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 14, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Regresar a preparación</button>
      )}
    </article>
  );
}
